<template>
    <Modal id="hold-order-modal"
           icon="fa-burger-soda"
           title="KITCHEN_MODAL.SET_REF_MODAL_TITLE"
           :cancel="cancel">
        <template v-slot:default>
            <template v-if="kitchenCart">
                <CartOverview v-if="kitchenCart"
                              ref="stash"
                              :staff-ref-placeholder="staffRefPlaceholder"
                              :edit-ref="true"
                              :stashed-cart="kitchenCart"
                              :allow-table-view="true"
                              @view-tables="viewTables"
                              @enter-key-down="checkStaffRef"></CartOverview>
            </template>
        </template>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           :disabled="sendingToKitchen || printingBill"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>
                <SquareBtn size="lg"
                           :loading="printingBill"
                           :disabled="sendingToKitchen || !refIsValid"
                           @click="printBill">
                    {{ l('KITCHEN_MODAL.PRINT_BILL_BUTTON') }}
                </SquareBtn>
                <SquareBtn size="lg"
                           :disabled="!refIsValid"
                           :loading="settingRef"
                           @click="setReference">
                    {{ l('ORDERS_ON_HOLD.SET_REFERENCE_BUTTON') }}
                </SquareBtn>
                <SquareBtn color="primary"
                           size="lg"
                           :loading="sendingToKitchen"
                           :disabled="!refIsValid"
                           @click="checkStaffRef">
                    {{ l('KITCHEN_MODAL.SEND_TO_KITCHEN_BUTTON') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import CartOverview from '@/epos/vue/modals/common/CartOverview';
import Modal from '@/vue/components/Modals/Modal';
import OrderRequest from '@/lib/Orders/OrderRequest';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { mergeCarts } from '@/lib/Cart/mergeCarts';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'SendToKitchenModal',

    components: {
        Modal,
        SquareBtn,
        CartOverview,
    },

    mixins: [
        ModalPropsMixin,
    ],

    data() {
        return {
            kitchenCart: null,
            printingBill: false,
            sendingToKitchen: false,
            settingRef: false,
            staffRefPlaceholder: 'ORDERS.STAFF_REF_REQUIRED_PLACEHOLDER',
        };
    },

    computed: {
        refIsValid() {
            return !container.userService.currentBranch.kitchenRefRequired || !!this.kitchenCart.currentCart.staffRef;
        },
    },

    created() {
        this.kitchenCart = container.cartStashHelperService.cartToStash(container.cartService);
    },

    mounted() {
        this.$nextTick(() => {
            // Focus on the reference input on load.
            if (this.$refs.stash && this.$refs.stash.$refs['ref-input']) {
                this.$refs.stash.$refs['ref-input'].focus();
            }
        });
    },

    methods: {
        printBill() {
            this.assignEnteredRefToCurrentCart();

            let order = container.cartService.toOrder();
            container.receiptPrinter.printBill(order);

            this.resolve();
        },

        async checkStaffRef() {
            if (!this.refIsValid) {
                return;
            }
            this.sendingToKitchen = true;

            // We only need to check for duplicate staffRefs if the user has entered a staffRef.
            if (this.kitchenCart.currentCart.staffRef) {
                // Fetch any existing activeFulfillmentOrders.
                const orders = await container.orderService.findActiveFulfillmentOrders();
                const matchedOrder = orders.find((kitchenOrderAsStashedCart) => {
                    return (
                        kitchenOrderAsStashedCart.currentCart.staffRef === this.kitchenCart.currentCart.staffRef
                        // If a cart has an originalOrder then it is an edit of an existing order so the merge question
                        // has already been asked so we don't need to ask again.
                        && !this.kitchenCart.currentCart.originalOrder
                    );
                });

                if (matchedOrder !== undefined) {
                    const mergedCart = await this.mergeCartIntoOrderModal(this.kitchenCart, matchedOrder);
                    if (mergedCart === undefined) {
                        // The operator pressed back.
                        return;
                    }
                    if (mergedCart) {
                        container.cartService.currentCart = mergedCart;
                    } else {
                        // We still need to save the cart but the operator decided not to merge.
                    }
                }
            }
            this.sendToKitchen();
        },

        /**
         * Resolves with a merged Cart from the 2 StashedCart's Carts if the user chooses to accept.
         *
         * @param {StashedCart} newStashedCart
         * @param {StashedCart} existingStashedCart
         *
         * @return {Promise<Cart>}
         */
        mergeCartIntoOrderModal(newStashedCart, existingStashedCart) {
            return container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.MERGE_CART,
                    {
                        isKitchenOrderMerge: true,
                        newStashedCart,
                        existingStashedCart,
                    },
                )
                .then((shouldMerge) => {
                    if (shouldMerge) {
                        return mergeCarts(existingStashedCart.currentCart, newStashedCart.currentCart);
                    }
                    return null;
                }).catch(() => {
                    // The operator has closed the merge modal by clicking the back button.
                    this.sendingToKitchen = false;
                });
        },

        assignEnteredRefToCurrentCart() {
            container.cartService.setStaffRef(this.kitchenCart.currentCart.staffRef);
        },

        sendToKitchen() {
            this.assignEnteredRefToCurrentCart();

            let request = new OrderRequest(
                container.cartToConfirmationStructureMapper.mapNewOrderOrAdjustment(container.cartService.currentCart),
                [],
                container.cartToReservationStructureMapper.removeZeroQtyLines(
                    container.cartToReservationStructureMapper.map(container.cartService.currentCart),
                ),
            );

            // Append a flag to the request data so that we can easily distinguish orders that are created or updated
            // as a result of clicking the 'Send To Kitchen' button as opposed to clicking the 'Pay' button.
            // This flag is not used by the API but shows up and can be filtered on in the logs.
            request.reservation.sendToKitchenPressed = 1;
            request.order.sendToKitchenPressed = 1;

            container.orderRequestSubmitter.submit(request)
                .then(
                    /**
                     * @param {OrderResponse|OrderAdjustmentResponse} result
                     */
                    async (result) => {
                        let createdOrder;
                        if (result.adjustedOrder) {
                            createdOrder = result.adjustedOrder;
                        } else {
                            // The API order creation endpoint does not return enough info about the order.
                            // We could piece it together from the cart but it's easier to just fetch the order again.
                            createdOrder = await container.orderSearcher.remoteGet(result.order.ID);
                        }

                        await container.itemRepository.attachItemsToOrderLines(
                            createdOrder.getLines(),
                        );

                        this.resolve({
                            createdOrder,
                            sentToKitchen: true,
                            restoredKitchenOrder: container.cartService.currentCart.restoredKitchenOrder,
                        });
                    }
                )
                .catch(() => {
                    this.resolve({
                        sentToKitchen: false,
                    });
                });
        },

        viewTables() {
            container.vueModalFactory.open(MODAL_NAMES__EPOS.TABLE_PICKER, { resolveOnly: true })
                .then((res) => {
                    if (res && res.value) {
                        this.kitchenCart.currentCart.staffRef = res.value;
                    }
                });
        },

        setReference() {
            this.settingRef = true;

            const newRef = this.kitchenCart.currentCart.staffRef;
            return container.staffRefValidator
                .setCartStaffRef(container.cartService.currentCart, newRef)
                .then(() => {
                    this.resolve({
                        refSet: true,
                    });
                })
                .finally(() => {
                    this.settingRef = false;
                });
        },
    },
};
</script>
